import { useCallback, useEffect, useRef, useState } from "react";
import type { ReactNode } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { api, setViewingOrgId } from "../../api/client";
import { ClientRoutes } from "../../components/ClientRoutes";
import { Spinner } from "../../components/Spinner";
import { ExperienceProvider, useExperience } from "../../context/ExperienceContext";
import { FrameworksProvider, useFrameworks } from "../../context/FrameworksContext";
import type { FrameworkId } from "../../context/FrameworksContext";
import { PortalNavContext, PortalPrefixContext, prefixPortalPath } from "../../context/PortalNavContext";
import type { PortalNavigate } from "../../context/PortalNavContext";
import { onAddonsChanged } from "../../nav/addonsChanged";
import { AppShell } from "../../nav/AppShell";
import { Breadcrumb } from "../../nav/AppTopBar";
import { buildClientNav, buildSetupChecklist, NO_ADDONS } from "../../nav/clientNav";
import type { ClientAddons, DataProtectionScope } from "../../nav/clientNav";
import { clientOverviewPath, clientPortalPath } from "../../nav/consultantNav";
import { usePrefixedClientAdapter } from "../../nav/LinkAdapter";
import type { NavCounts } from "../../nav/NavCountsContext";
import type { NavModel, NavSection } from "../../nav/types";
import { card, theme } from "../../theme";
import { ClientSwitcher } from "./ClientSwitcher";
import { useConsultantShell } from "./ConsultantShellContext";
import { useConsultantSwitches } from "./useConsultantSwitches";
import { useCurrentUser } from "../../auth/useCurrentUser";

// ── Consultant read-through of a client portal ────────────────────────────
// Mounted at /consultant/clients/:orgId/portal/*. Scopes every API call to
// the client (setViewingOrgId), provides PortalNavContext / PortalPrefixContext
// so client pages navigate and link inside the portal, and renders the client's own navigation
// (buildClientNav) through AppShell with a prefixed link adapter. The
// consultancy's own organisation never comes through here: it is a real
// client session (enterOwnOrgPortal).

interface OrgPortalState {
  name: string | null;
  tierLevel: number;
  tierName: string | null;
  addons: ClientAddons;
  /** Scope of the Data Protection page; null until the add-ons call answers. */
  dataProtection: DataProtectionScope | null;
  loaded: boolean;
}

const INITIAL_ORG_STATE: OrgPortalState = { name: null, tierLevel: 1, tierName: null, addons: NO_ADDONS, dataProtection: null, loaded: false };

/**
 * Tier level, tier name, name and add-on flags of the client being viewed
 * (the same two calls the client shell makes, scoped by setViewingOrgId),
 * refetched whenever My Services changes an add-on.
 */
function useOrgPortalState(orgId: string): OrgPortalState {
  const [state, setState] = useState<OrgPortalState>(INITIAL_ORG_STATE);
  useEffect(() => {
    let cancelled = false;
    setState(INITIAL_ORG_STATE);
    const load = () => {
      Promise.all([
        api.get<{ addons: Partial<ClientAddons>; dataProtection?: DataProtectionScope }>("/organizations/addons").catch(() => null),
        api.get<{ organization: { name: string; isDemo?: boolean; serviceTier: { level: number; name: string } } }>("/organizations/profile").catch(() => null),
      ]).then(([addons, profile]) => {
        if (cancelled) return;
        setState({
          name: profile?.organization?.name ?? null,
          // A consultant may use every feature of a demo organisation (backend requireTier), so nothing is shown locked.
          tierLevel: profile?.organization?.isDemo ? 5 : (profile?.organization?.serviceTier?.level ?? 1),
          tierName: profile?.organization?.serviceTier?.name ?? null,
          addons: addons ? { ...NO_ADDONS, ...addons.addons, hardening: addons.addons.hardening ?? false } : NO_ADDONS,
          dataProtection: addons?.dataProtection ?? null,
          loaded: true,
        });
      });
    };
    load();
    const unsubscribe = onAddonsChanged(load);
    return () => { cancelled = true; unsubscribe(); };
  }, [orgId]);
  return state;
}

function PortalShell({ orgId, children }: { orgId: string; children: ReactNode }) {
  const shell = useConsultantShell();
  const { enrolled, pciSaqTypes } = useFrameworks();
  const org = useOrgPortalState(orgId);
  const listed = shell.clients.find((c) => c.id === orgId) ?? null;
  const orgName = org.name ?? listed?.name ?? null;
  const adapter = usePrefixedClientAdapter(clientPortalPath(orgId));
  const switches = useConsultantSwitches();
  // /auth/me answered for this organisation (the viewing org rides on every request): in a demo
  // organisation every consultant holds the full set (backend demoClientContext), elsewhere it
  // is the consultant's own set — the same list the pages inside read.
  const { user: portalUser } = useCurrentUser();
  const permissions = portalUser?.permissions ?? shell.permissions;
  const isAdmin = permissions.includes("org:admin");

  // The client just opened goes to the top of "Recent clients" (the firm's own
  // clients only; ids are what is stored).
  const { recordRecentClient } = shell;
  const isListed = listed !== null;
  useEffect(() => {
    if (isListed) recordRecentClient(orgId);
  }, [orgId, isListed, recordRecentClient]);

  // `enrolled` is a fresh Set every render; key the memo on its contents.
  const enrolledKey = Array.from(enrolled).sort().join(",");
  const saqKey = pciSaqTypes.join(",");
  const permissionsKey = permissions.join(",");
  const title = orgName ?? "Client portal";
  const { novice: guided } = useExperience();

  const buildModel = useCallback((counts: NavCounts | null): NavModel => {
    const client = counts?.role === "client" ? counts : null;
    const back: NavSection = {
      id: "consultant",
      items: [
        // Exact: every read-through URL sits below /consultant/clients/:orgId.
        { id: "consultant-view", label: "Consultant view", to: clientOverviewPath(orgId), exact: true, icon: ArrowLeft, keywords: ["back", "manage", "consultant"] },
      ],
    };
    return {
      shellKey: "consultant-portal",
      header: {
        title,
        subtitle: org.tierName ?? "Client portal",
        role: { label: "Client portal", tone: "client" },
      },
      sections: [
        back,
        ...buildClientNav({
          // Unknown until the profile answers: no lock affordance meanwhile.
          tierLevel: org.loaded ? org.tierLevel : null,
          addons: org.addons,
          enrolled: enrolledKey ? (enrolledKey.split(",") as FrameworkId[]) : [],
          pciSaqTypes: saqKey ? saqKey.split(",") : [],
          permissions: permissionsKey ? permissionsKey.split(",") : [],
          counts: client,
          publicApiEnabled: client?.publicApiEnabled ?? false,
          frameworkScores: client?.frameworkScores ?? [],
          dataProtection: org.dataProtection,
          guided,
        }),
      ],
      setup: isAdmin ? buildSetupChecklist(client?.setup) : undefined,
    };
  }, [orgId, title, org.tierName, org.tierLevel, org.loaded, org.addons, org.dataProtection, enrolledKey, saqKey, permissionsKey, isAdmin, guided]);

  const breadcrumb = (
    <>
      <Breadcrumb items={[
        { label: "Clients", to: "/consultant/clients" },
        { label: title, to: clientOverviewPath(orgId) },
        { label: "Client portal" },
      ]} />
      <ClientSwitcher orgId={orgId} currentName={orgName} mode="portal" />
    </>
  );

  return (
    <AppShell
      model={buildModel}
      adapter={adapter}
      user={shell.user}
      tierLevel={org.tierLevel}
      countsKey={`consultant-portal:${orgId}`}
      moreToolsTo="/more-tools"
      moreToolsSection="account"
      recordSearch
      topBar={{ breadcrumb, bell: "alerts" }}
      userMenu={{ switches, showExperienceMode: true, versionTo: "/consultant/whats-new" }}
    >
      {children}
    </AppShell>
  );
}

export function ConsultantClientPortal() {
  const { orgId = "" } = useParams<{ orgId: string }>();
  const navigate = useNavigate();
  const { selfOrg, enterOwnOrgPortal, portfolio, user } = useConsultantShell();
  // Whether this is the consultancy's own organisation is only known once the
  // portfolio has settled; until then nothing renders and nothing is scoped.
  const settled = portfolio.loaded;
  const isOwnOrg = !!selfOrg && selfOrg.id === orgId;
  const readThrough = settled && !isOwnOrg;

  // Every API call made by the client pages and their providers must carry
  // ?organizationId=. Child effects run before this component's own effect, so
  // the id is set synchronously during render (before any child fetch), again
  // after commit (StrictMode replays effects after a simulated unmount), and
  // cleared only when the read-through really unmounts. Switching client keeps
  // the id set throughout, so a remounting FrameworksProvider never fetches
  // unscoped. ConsultantWorkspace clears it on its own render as well. The own
  // organisation is never scoped: it opens as a real client session below.
  const applied = useRef<string | null>(null);
  if (readThrough && orgId && applied.current !== orgId) {
    setViewingOrgId(orgId);
    applied.current = orgId;
  }
  useEffect(() => {
    if (!readThrough || !orgId) return;
    setViewingOrgId(orgId);
    applied.current = orgId;
  }, [readThrough, orgId]);
  useEffect(() => () => {
    setViewingOrgId(null);
    applied.current = null;
  }, []);

  // The consultancy's own organisation is a real client session, never a
  // read-through. A failed switch is reported instead of spinning forever.
  const switching = useRef(false);
  const [switchFailed, setSwitchFailed] = useState(false);
  const attemptSwitch = useCallback(() => {
    if (switching.current) return;
    switching.current = true;
    setSwitchFailed(false);
    void enterOwnOrgPortal()
      .then((ok) => { if (!ok) setSwitchFailed(true); })
      .finally(() => { switching.current = false; });
  }, [enterOwnOrgPortal]);
  useEffect(() => {
    if (settled && isOwnOrg) attemptSwitch();
  }, [settled, isOwnOrg, attemptSwitch]);

  const prefix = clientPortalPath(orgId);
  const portalNavigate = useCallback<PortalNavigate>((path, options) => {
    navigate(prefixPortalPath(prefix, path), options?.replace ? { replace: true } : undefined);
  }, [navigate, prefix]);

  if (!orgId) return <Navigate to="/consultant/clients" replace />;

  if (!readThrough) {
    const failed = isOwnOrg && switchFailed;
    return (
      <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: theme.color.bg, color: theme.color.muted, fontFamily: theme.font, fontSize: 13, padding: 16 }}>
        {failed ? (
          <div role="alert" style={{ ...card, textAlign: "center", padding: "20px 22px", maxWidth: 360 }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: theme.color.heading }}>Could not open your company's portal.</div>
            <div style={{ display: "flex", gap: 8, justifyContent: "center", marginTop: 14, flexWrap: "wrap" }}>
              <button
                type="button"
                onClick={attemptSwitch}
                style={{ background: theme.color.accent, color: "#fff", border: "none", borderRadius: 3, padding: "7px 14px", fontSize: 12.5, fontWeight: 600, cursor: "pointer", fontFamily: theme.font }}
              >
                Try again
              </button>
              <button
                type="button"
                onClick={() => navigate(clientOverviewPath(orgId))}
                style={{ background: "transparent", color: theme.color.accentText, border: `1px solid ${theme.color.border}`, borderRadius: 3, padding: "7px 14px", fontSize: 12.5, fontWeight: 600, cursor: "pointer", fontFamily: theme.font }}
              >
                Back to client overview
              </button>
            </div>
          </div>
        ) : (
          <div style={{ textAlign: "center" }}>
            <Spinner size={28} />
            <div style={{ marginTop: 14 }}>{isOwnOrg ? "Opening your company's portal" : "Loading"}</div>
          </div>
        )}
      </div>
    );
  }

  // A link to a client the firm does not have (a typo, a deleted client) says so, as the client
  // overview does, rather than opening onto pages that read like a new organisation's onboarding.
  // A platform administrator may reach organisations outside the firm's portfolio, so not for them.
  if (!user.isPlatformOwner && !portfolio.orgs.some((o) => o.id === orgId)) {
    return (
      <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: theme.color.bg, fontFamily: theme.font, padding: 16 }}>
        <div role="alert" style={{ ...card, textAlign: "center", padding: "20px 22px", maxWidth: 380 }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: theme.color.heading }}>This client could not be found.</div>
          <div style={{ fontSize: 12.5, color: theme.color.subtle, marginTop: 6 }}>It may have been removed, or the link is wrong.</div>
          <button
            type="button"
            onClick={() => navigate("/consultant/clients")}
            style={{ marginTop: 14, background: theme.color.accent, color: "#fff", border: "none", borderRadius: 3, padding: "7px 14px", fontSize: 12.5, fontWeight: 600, cursor: "pointer", fontFamily: theme.font }}
          >
            All clients
          </button>
        </div>
      </div>
    );
  }

  return (
    <PortalNavContext.Provider value={portalNavigate}>
      <PortalPrefixContext.Provider value={prefix}>
        <ExperienceProvider>
          <FrameworksProvider key={prefix}>
            <PortalShell orgId={orgId}>
              <ClientRoutes />
            </PortalShell>
          </FrameworksProvider>
        </ExperienceProvider>
      </PortalPrefixContext.Provider>
    </PortalNavContext.Provider>
  );
}
